import { Customer, CustomerAddress } from '@lambda/apis/src/customer/types';
import { DefaultValuesOption } from '@stripe/stripe-js';
import dayjs from 'dayjs';
import {
  CheckoutDiscountT,
  CheckoutT,
  CheckoutLineT,
  DiscountType,
} from '@lambda/apis/src/checkout/types';
import { LineItemType } from '@lambda/apis/src/cart/types';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import * as lookup from 'country-code-lookup';
import { NAVIGATION } from './constants';
import settings from '@/settings';
import { CheckoutStep } from './enums';
import { logger } from '../logger';
import { formSchema } from '@/components/checkout/AddressForm/constants';
import i18n from '@/settings/i18n';

/**
 * Phone number is optional for addresses
 * @param address
 * @returns
 */

const restrictedStates = {
  AK: 'Alaska',
  AS: 'American Samoa',
  AE: 'Armed Forces Africa',
  AA: 'Armed Forces Americas',
  AP: 'Armed Forces Pacific',
  MP: 'Commonwealth of the Northern Mariana Islands',
  FM: 'Federated States of Micronesia',
  GU: 'Guam',
  HI: 'Hawaii',
  PR: 'Puerto Rico',
  PW: 'Republic of Palau',
  MH: 'Republic of the Marshall Islands',
  VI: 'US Virgin Islands',
};

const restrictedCanadaProvinces = {
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  YT: 'Yukon',
};

// Northern Ireland (BT postcodes) is UK territory, served by reebelo.co.uk (RT-17519).
// Mirrors the server-side block in MsCheckout; Eircodes never start with "BT".
const isRestrictedPostalCode = (postalCode: string | undefined): boolean =>
  settings.store === 'reebelo-ie' &&
  /^BT\s*\d/i.test((postalCode ?? '').trim());

export enum AddressValidationErrorMessages {
  NORTHERN_IRELAND = "Sorry, we can't deliver to Northern Ireland (BT postcodes) from Reebelo Ireland. Please shop on reebelo.co.uk instead.",
  MADEIRA_AZORES = "Sorry, we can't deliver to Madeira or the Azores yet. Please try a mainland Portugal address.",
  ALAND = "Sorry, we can't deliver to the Åland Islands yet. Please try a mainland Finland address.",
  RESTRICTED_STATE = "Sorry. We don't deliver to your address yet. Please try a different address",
  PO_BOX = "Unfortunately, we don't deliver to PO box addresses. Please try with a different address.",
  DEFAULT = 'Please provide the missing or correct information to complete the order.',
}

// Madeira/Azores (PT 9000-9980) and Åland (FI 22xxx) are not served yet (RT-17726, RT-17760).
// Mirrors getRestrictedRegion in MsCheckout; mainland postcodes are unaffected.
const getRestrictedRegionError = (
  postalCode: string | undefined,
): AddressValidationErrorMessages | null => {
  const code = (postalCode ?? '').trim();

  if (
    settings.store === 'reebelo-pt' &&
    /^9\d{3}(?:[-\s]?\d{3})?$/.test(code)
  ) {
    return AddressValidationErrorMessages.MADEIRA_AZORES;
  }

  if (settings.store === 'reebelo-fi' && /^22\d{3}$/.test(code)) {
    return AddressValidationErrorMessages.ALAND;
  }

  return null;
};

export const handleRestrictedStates = (
  state: string | undefined,
  postalCode?: string,
): string | null => {
  const { store } = settings;

  if (isRestrictedPostalCode(postalCode)) {
    return AddressValidationErrorMessages.NORTHERN_IRELAND;
  }

  const restrictedRegionError = getRestrictedRegionError(postalCode);

  if (restrictedRegionError) return restrictedRegionError;

  // Check for restricted US states
  if (
    ['reebelo-us', 'reebelo-dev'].includes(store) &&
    state &&
    restrictedStates[state as keyof typeof restrictedStates]
  ) {
    return AddressValidationErrorMessages.RESTRICTED_STATE;
  }

  if (
    ['reebelo-ca', 'reebelo-dev'].includes(store) &&
    state &&
    restrictedCanadaProvinces[state as keyof typeof restrictedCanadaProvinces]
  ) {
    return AddressValidationErrorMessages.RESTRICTED_STATE;
  }

  return null;
};

const poBoxPatterns = [
  /p\.?\s*o\.?\s*box/i, // P.O. Box, PO Box, P O Box
  /post\s*office\s*box/i, // Post Office Box
  /p\.?\s*o\.?\s*b\.?\s*#?\s*\d+/i, // P.O.B. #123, POB 123
  /box\s*#?\s*\d+/i, // Box #123, Box 123
  /bo[iî]te\s*postale/i, // Boîte Postale
  /^#?\s*\d+\s*p\.?\s*o\.?\s*b/i, // #123 POB
  /^p\.?\s*o\.?\s*b\.?\s*\d+/i, // POB 123
  /^bp\s*\d+/i, // BP 123
  /[\u2119][\u24DE]\s*box/i, // Unicode P O Box
];

const isPOBox = (address?: CustomerAddress): boolean => {
  // Verify address object and normalize address lines
  if (!address || typeof address !== 'object') {
    return false;
  }

  const cleanAddress1 = String(address.address1 || '')
    .trim()
    .normalize('NFKC');
  const cleanAddress2 = String(address.address2 || '')
    .trim()
    .normalize('NFKC');

  // Test both address lines against all patterns
  return poBoxPatterns.some(
    (pattern) => pattern.test(cleanAddress1) || pattern.test(cleanAddress2),
  );
};

export const isAddressValid = (
  address?: CustomerAddress | undefined,
): {
  valid: boolean;
  errorMessage: AddressValidationErrorMessages | string | null;
} => {
  const state = address?.state;
  const { store } = settings;

  if (isRestrictedPostalCode(address?.postalCode)) {
    return {
      valid: false,
      errorMessage: AddressValidationErrorMessages.NORTHERN_IRELAND,
    };
  }

  const restrictedRegionError = getRestrictedRegionError(address?.postalCode);

  if (restrictedRegionError) {
    return { valid: false, errorMessage: restrictedRegionError };
  }

  if (
    ['reebelo-us', 'reebelo-dev'].includes(store) &&
    state &&
    restrictedStates[state as keyof typeof restrictedStates]
  ) {
    return {
      valid: false,
      errorMessage: AddressValidationErrorMessages.RESTRICTED_STATE,
    };
  }

  if (
    ['reebelo-ca', 'reebelo-dev'].includes(store) &&
    state &&
    restrictedCanadaProvinces[state as keyof typeof restrictedCanadaProvinces]
  ) {
    return {
      valid: false,
      errorMessage: AddressValidationErrorMessages.RESTRICTED_STATE,
    };
  }

  if (
    ['reebelo-us', 'reebelo-ca', 'reebelo-dev'].includes(store) &&
    isPOBox(address)
  ) {
    return {
      valid: false,
      errorMessage: AddressValidationErrorMessages.PO_BOX,
    };
  }

  if (!address) {
    return {
      valid: false,
      errorMessage: AddressValidationErrorMessages.DEFAULT,
    };
  }

  // Stripe elements submit is responsible now for field level validation
  return {
    valid: true,
    errorMessage: null,
  };
};

/**
 * Phone number and address2 are optional for addresses
 * @param address1
 * @param address2
 * @returns
 */
export const addressesAreEqual = (
  address1: CustomerAddress,
  address2: CustomerAddress,
) => {
  const equal =
    address1.firstName === address2.firstName &&
    address1.lastName === address2.lastName &&
    address1.address1 === address2.address1 &&
    address1.city === address2.city &&
    address1.country === address2.country &&
    address1.state === address2.state &&
    address1.postalCode === address2.postalCode &&
    (address1.phone === address2.phone || !address1.phone || !address2.phone) &&
    (address1.address2 === address2.address2 ||
      !address1.address2 ||
      !address2.address2);

  logger.debug(
    { address1, address2, equal },
    'Determined if addresses are equal',
  );

  return equal;
};

export const generatePageTitle = (
  step?: CheckoutStep,
  error?: string | null,
  processing?: boolean,
) => {
  const t = i18n.init('checkout');

  if (!step) {
    return t`Loading - Reebelo ${{
      country: t(settings.country),
    }} - Checkout`;
  }

  const currentStepLabel = NAVIGATION.find((nav) => nav.value === step)?.label;

  let prefix = '';

  if (processing) {
    prefix = t`Processing - `;
  } else if (error != null) {
    prefix = t`Error - `;
  }

  return `${prefix}${t`${{ step: t(currentStepLabel ?? '') }} - Reebelo ${{
    country: t(settings.country),
  }} - Checkout`}`;
};

export const generateDefaultValuesOption = (
  customer: Customer,
): DefaultValuesOption => ({
  billingDetails: {
    name: `${customer.billingAddress?.firstName} ${customer.billingAddress?.lastName}`,
    email: customer.email,
    phone: customer.billingAddress?.phone,
    address: {
      country: customer.billingAddress?.country,
      postal_code: customer.billingAddress?.postalCode,
      state: customer.billingAddress?.state,
      city: customer.billingAddress?.city,
      line1: customer.billingAddress?.address1,
      line2: customer.billingAddress?.address2 || undefined,
    },
  },
});

export const findDeliveryRange = (
  dates: { deliverMin?: string; deliverMax?: string }[],
): { earliestMin?: string; latestMax?: string } => {
  let earliestMin: dayjs.Dayjs | undefined;
  let latestMax: dayjs.Dayjs | undefined;

  dates.forEach(({ deliverMin, deliverMax }) => {
    if (deliverMin) {
      const minDate = dayjs(deliverMin);

      if (!earliestMin || minDate.isBefore(earliestMin)) {
        earliestMin = minDate;
      }
    }

    if (deliverMax) {
      const maxDate = dayjs(deliverMax);

      if (!latestMax || maxDate.isAfter(latestMax)) {
        latestMax = maxDate;
      }
    }
  });

  return {
    earliestMin: earliestMin?.format('DD MMM'),
    latestMax: latestMax?.format('DD MMM'),
  };
};

export const getUniqueDiscountCodes = (
  checkout: CheckoutT,
): CheckoutDiscountT[] => {
  const discounts = (checkout.lineItems || [])
    .filter((line) => line.discounts?.length)
    .flatMap((line) => line.discounts)
    .concat(checkout.discounts)
    .filter((discount) => discount?.applicable);

  return discounts.reduce((acc, discount) => {
    if (!discount?.code) {
      return acc;
    }

    const existingDiscount = acc.find(
      (d) => d?.code && d.code === discount.code,
    );

    if (existingDiscount) {
      existingDiscount.amount += discount.amount;
    } else {
      acc.push({ ...discount });
    }

    return acc;
  }, [] as CheckoutDiscountT[]);
};

export const getDiscountLabel = (discount: CheckoutDiscountT): string => {
  if (discount.type === DiscountType.PROMOTION_TIER) {
    return discount.name || discount.code;
  }

  return discount.code;
};

export const generateCategoriesConditions = (lineItems: CheckoutLineT[]) => {
  if (!lineItems.length) {
    return {
      categoryPks: [],
      conditions: [],
    };
  }

  const conditions: string[] = [];

  lineItems.forEach((item) => {
    const extractedTags = extractTags(
      item.notes?.map((note) => note.value) ?? [],
    );

    conditions.push(extractedTags.condition ?? '');
  });

  const categoryPks = lineItems
    .map((item) => {
      const categoryPk = item.notes?.find(
        (attribute) => attribute.name === '_category_pk',
      )?.value;

      return categoryPk;
    })
    .filter((categoryPk): categoryPk is string => Boolean(categoryPk));

  return {
    categoryPks,
    conditions,
  };
};

export const findOutOfStockCheckoutLineItems = (lineItems: CheckoutLineT[]) => {
  if (!lineItems || !Array.isArray(lineItems) || lineItems.length === 0) {
    logger.warn('No line items provided or invalid format');

    return [];
  }

  const outOfStockLineItems: CheckoutLineT[] = [];

  lineItems.forEach((lineItem) => {
    const { type, quantity, inventory } = lineItem;

    logger.log(
      `Checking OOS for line item, Type: ${type}, Quantity: ${quantity}, Inventory:`,
      inventory,
    );

    if (type?.toLocaleLowerCase() !== LineItemType.PRODUCT) {
      return;
    }

    const available = inventory?.available ?? 0;

    if (available < quantity) {
      outOfStockLineItems.push(lineItem);
      logger.log(`Out of stock: ${lineItem.id}`);
    }
  });

  logger.log('Out of stock line items:', outOfStockLineItems);

  return outOfStockLineItems;
};

export const getCountryCode = (countryName: string) => {
  if (!countryName) {
    return undefined;
  }

  const country = lookup.byCountry(countryName) || lookup.byIso(countryName);

  return country?.iso2;
};

export const getState = (state: string): string =>
  formSchema[settings.store]?.state.required ? state : state || '';
